import { HttpError } from "@/api/apiFetch";
import { useGetCourseCompletionQuizQuestions } from "@/api/generated/course-completion-quiz/course-completion-quiz";
import type { CompletionQuizQuestions } from "@/types/courseCompletionQuiz";

interface CourseCompletionQuizRun {
  /** The open run with its questions, or null when no run is open. */
  run: CompletionQuizQuestions | null;
  isLoading: boolean;
  error: Error | undefined;
}

/** The backend answers 422 when the quiz has not been started or is over. */
const meansNoRunIsOpen = (error: unknown) =>
  error instanceof HttpError && error.status === 422;

/**
 * Loads the open run of the course completion quiz for the run page. Always
 * asked again on arrival: what has been answered is only on the server.
 */
const useCourseCompletionQuizRun = (
  enrollmentId: string | undefined
): CourseCompletionQuizRun => {
  const { data, error, isLoading } = useGetCourseCompletionQuizQuestions<Error>(
    enrollmentId ?? "",
    {
      swr: {
        enabled: Boolean(enrollmentId),
        revalidateOnFocus: false,
        dedupingInterval: 0,
        shouldRetryOnError: (failure) => !meansNoRunIsOpen(failure),
      },
    }
  );

  if (meansNoRunIsOpen(error)) {
    return { run: null, isLoading, error: undefined };
  }

  return { run: data ?? null, isLoading, error };
};

export { useCourseCompletionQuizRun };
